import React, { useRef } from 'react';
import classNames from 'classnames';
import ReactTextareaAutosize from 'react-textarea-autosize';

import { withFormik } from 'formik';
import {
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Button,
  FormFeedback,
  FormText,
} from 'reactstrap';
import * as Yup from 'yup';
import ReactMarkdown from 'react-markdown';

import AssessmentTypeSelect from './AssessmentTypeSelect';
import styles from './AssessmentEditorComponent.module.scss';

const placeholderOptions = [
  {
    name: 'e.g. Team Skills Assessment',
    description:
      'e.g. Measure and track key competencies across your organization',
  },
  {
    name: 'e.g. Leadership Development Survey',
    description:
      'e.g. Evaluate management capabilities and identify growth opportunities',
  },
  {
    name: 'e.g. Project Readiness Check',
    description:
      'e.g. Assess team preparedness and capability gaps for upcoming initiatives',
  },
  {
    name: 'e.g. Professional Growth Tracker',
    description:
      'e.g. Monitor progress on core skills and professional development goals',
  },
];

/**
 * The type is fixed at creation because the two kinds of assessment offer
 * different question styles, and switching later would invalidate every
 * question already authored.
 */
const ASSESSMENT_TYPES = [
  {
    value: 'survey',
    name: 'Survey',
    description: 'Participants answer on their own time, wherever they are.',
  },
  {
    value: 'live',
    name: 'Live assessment',
    description:
      'A facilitator runs the assessment with everyone at once, together in a room or on a video call. Offers a limited set of question types.',
  },
];

const CreateAssessmentForm = ({
  values,
  touched,
  errors,
  handleChange,
  handleBlur,
  handleSubmit,
  setFieldValue,
  setFieldTouched,
}) => {
  const placeholderRef = useRef(null);
  if (placeholderRef.current === null) {
    placeholderRef.current =
      placeholderOptions[Math.floor(Math.random() * placeholderOptions.length)];
  }
  const placeholders = placeholderRef.current;

  const selectedType = ASSESSMENT_TYPES.find((t) => t.value === values.type);

  const formikAttributes = (fieldName) => ({
    id: fieldName,
    name: fieldName,
    value: values[fieldName],
    onChange: handleChange,
    onBlur: handleBlur,
    invalid: errors[fieldName] && touched[fieldName],
    placeholder: placeholders[fieldName],
  });

  const formikFeedback = (fieldName) => {
    return errors[fieldName] && touched[fieldName] ? (
      <FormFeedback>{errors[fieldName]}</FormFeedback>
    ) : null;
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Row>
        <Col sm={4}>
          <FormGroup>
            <Label for="name">Name</Label>
            <Input type="text" {...formikAttributes('name')} autoFocus />
            {formikFeedback('name')}
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col sm={4}>
          <FormGroup>
            <Label for="type">Type</Label>
            <AssessmentTypeSelect
              types={ASSESSMENT_TYPES}
              value={values.type}
              onChange={(type) => setFieldValue('type', type)}
              onBlur={() => setFieldTouched('type', true)}
              invalid={!!(errors.type && touched.type)}
            />
            {/* Repeated below the closed select so the guidance is readable
                without reopening the menu. */}
            <FormText>{selectedType?.description}</FormText>
            {/* `d-block` because Bootstrap only reveals feedback that directly
                follows the invalid control, and here the control is nested. */}
            {errors.type && touched.type ? (
              <FormFeedback className="d-block">{errors.type}</FormFeedback>
            ) : null}
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col sm={12} lg={6}>
          <FormGroup>
            <Label for="description">
              Description (supports{' '}
              <a
                href="https://www.markdownguide.org/basic-syntax/"
                target="_blank"
                rel="noreferrer"
              >
                markdown
              </a>
              )
            </Label>
            <ReactTextareaAutosize
              style={{ maxHeight: 'unset' }}
              className={classNames('form-control', {
                'is-invalid': errors['description'] && touched['description'],
              })}
              minRows={4}
              {...formikAttributes('description')}
            />
            {formikFeedback('description')}
          </FormGroup>
        </Col>
        <Col sm={12} lg={6}>
          <Label for="description">Description Preview</Label>
          <ReactMarkdown className={styles.preview}>
            {values.description}
          </ReactMarkdown>
        </Col>
      </Row>
      <Row className="mt-3">
        <Col>
          <Button color="primary" type="submit" className="float-right">
            Create Assessment
          </Button>
          <Button
            color="secondary"
            className="float-right mr-2"
            onClick={() => window.history.back()}
          >
            Cancel
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

const FormikCreateAssessmentForm = withFormik({
  mapPropsToValues: () => ({
    name: '',
    description: '',
    type: 'survey',
  }),
  validationSchema: Yup.object().shape({
    type: Yup.string()
      .oneOf(
        ASSESSMENT_TYPES.map((t) => t.value),
        'Choose whether this is a survey or a live assessment.'
      )
      .required('Choose whether this is a survey or a live assessment.'),
    name: Yup.string()
      .trim()
      .required('Give your assessment a good name so people can identify it.')
      .max(
        250,
        'Your name is too long. Short and sweet is best (under 250 characters).'
      ),
    description: Yup.string()
      .trim()
      .required(
        'You need to provide a description so people can understand what this assessment is about.'
      ),
  }),
  handleSubmit: (values, { setSubmitting, props }) => {
    const { onSubmit = () => {} } = props;

    setSubmitting(true);
    onSubmit(values).finally(() => {
      setSubmitting(false);
    });
  },
  displayName: 'CreateAssessmentForm',
})(CreateAssessmentForm);

export default FormikCreateAssessmentForm;
